import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useBooking } from "@/context/BookingContext";
import { useContent } from "@/context/ContentContext";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/treatments", label: "Treatments" },
  { to: "/doctors", label: "Doctors" },
  { to: "/about", label: "About" },
  { to: "/gallery", label: "Gallery" },
  { to: "/blog", label: "Journal" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { book } = useBooking();
  const { content } = useContent();
  const location = useLocation();
  const announcement = content?.settings?.announcement;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <>
      {announcement && (
        <div className="fixed inset-x-0 top-0 z-50 bg-[#1A1615] text-center">
          <p data-testid="announcement-banner" className="truncate whitespace-nowrap px-4 py-2 text-[10px] uppercase tracking-[0.15em] text-[#F3E5AB] sm:text-[11px] sm:tracking-[0.18em]">
            {announcement}
          </p>
        </div>
      )}
      <div
        className={`fixed inset-x-0 z-40 flex justify-center px-3 transition-[top] duration-500 sm:px-4 ${
          announcement ? "top-11" : "top-4"
        }`}
      >
        <motion.nav
          initial={{ y: -40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          data-testid="main-navbar"
          className={`glass flex w-full max-w-6xl items-center justify-between rounded-full py-2 pl-3 pr-2 sm:py-2.5 sm:pl-4 sm:pr-3 ${
            scrolled
              ? "shadow-[0_12px_40px_rgba(212,175,55,0.18)]"
              : "shadow-[0_8px_32px_rgba(212,175,55,0.08)]"
          }`}
        >
          <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo-link" aria-label={`${content?.settings?.clinic_name || "Royal Skin Cosmetic Clinic"} home`}>
            <img src={content?.settings?.logo_url || "/assets/logo-mark.png"} alt="" className="h-9 w-auto object-contain sm:h-10" />
            <span className="hidden leading-none min-[420px]:block">
              <span className="font-serif text-lg font-semibold text-[#1A1615]">{(content?.settings?.clinic_name || "Royal Skin Cosmetic Clinic").replace(/\s*cosmetic\s*clinic\s*/i, "")}</span>
              <span className="block text-[8px] font-medium tracking-[0.3em] text-[#B38B2D]">COSMETIC CLINIC</span>
            </span>
          </Link>
          <div className="hidden items-center gap-6 lg:flex">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-[13px] font-medium tracking-wide transition-colors duration-300 hover:text-[#B38B2D] ${
                    isActive ? "text-[#B38B2D]" : "text-[#5A504B]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => book()}
              data-testid="nav-book-appointment-button"
              className="btn-gold hidden px-5 py-2.5 text-[13px] sm:inline-flex"
            >
              Book Consultation
            </button>
            <button
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="nav-mobile-menu-button"
              aria-label="Toggle navigation menu"
              className="flex h-11 w-11 items-center justify-center rounded-full text-[#1A1615] transition-colors hover:bg-[#D4AF37]/10 lg:hidden"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </motion.nav>
      </div>
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="glass fixed inset-x-4 top-24 z-40 rounded-3xl p-6 shadow-2xl lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `rounded-xl px-4 py-3 text-sm font-medium transition-colors ${
                      isActive ? "bg-[#D4AF37]/10 text-[#B38B2D]" : "text-[#1A1615] hover:bg-[#F5EBE6]"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <button
                onClick={() => {
                  setMobileOpen(false);
                  book();
                }}
                data-testid="mobile-book-appointment-button"
                className="btn-gold mt-3 px-5 py-3 text-sm"
              >
                Book Consultation
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
